// /client/src/components/Activity.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

const Activity = () => {
  const { user } = useAuth();
  
  // State management
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Filter state
  const [filters, setFilters] = useState({
    category: '',
    startDate: '',
    endDate: ''
  });

  // State for sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'createdAt',
    direction: 'descending'
  });

  const categoryOptions = ['contact', 'pod', 'product', 'program', 'message', 'auth', 'system', 'campaign', 'deployment', 'consent'];

  // Fetch activities
  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      // Add active filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`/api/activity?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setActivities(data.activities || []);
          setPagination(prev => ({
            ...prev,
            total: data.pagination?.total || 0,
            totalPages: data.pagination?.totalPages || 0
          }));
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch activities');
      }
    } catch (err) {
      setError('Failed to load activities');
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      category: '',
      startDate: '',
      endDate: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Sort activities client-side (server already sorts by createdAt)
  const sortedActivities = useMemo(() => {
    let sorted = [...activities];
    
    if (sortConfig !== null) {
      sorted.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        if (sortConfig.key === 'createdAt') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sorted;
  }, [activities, sortConfig]);

  const getSortIcon = (columnName) => {
    const iconStyle = {
      width: '12px',
      height: '12px',
      marginLeft: '8px',
      display: 'inline-block'
    };

    if (sortConfig.key !== columnName) {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5.83L15.17 9H13v6h-2V9H8.83L12 5.83zM12 18.17L8.83 15H11V9h2v6h2.17L12 18.17z"/>
        </svg>
      );
    }
    
    if (sortConfig.direction === 'ascending') {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 6.83L15.17 10H13v8h-2v-8H8.83L12 6.83z"/>
        </svg>
      );
    } else {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 17.17L8.83 14H11V6h2v8h2.17L12 17.17z"/>
        </svg>
      );
    }
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get status badge class
  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'is-success';
      case 'failure':
        return 'is-danger';
      case 'pending':
        return 'is-warning';
      default:
        return 'is-light';
    }
  };

  // Get category badge class
  const getCategoryColor = (category) => {
    const categoryColors = {
      contact: 'is-info',
      pod: 'is-primary',
      product: 'is-link',
      program: 'is-success',
      message: 'is-warning',
      auth: 'is-danger',
      system: 'is-dark',
      campaign: 'is-primary'
    };
    return categoryColors[category] || 'is-light';
  };

  const hasActiveFilters = filters.category || filters.startDate || filters.endDate;

  if (loading && activities.length === 0) {
    return (
      <>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-center'>
            <Navigation />
            <div className='mt-6 pt-6 container'>
              <div className="has-text-centered py-6">
                <div className="is-loading"></div>
                <p>Loading activity log...</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
              <h1 className='h1 my-2'>Activity Log</h1>
            </div>

            {error && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
                <br />
                <button 
                  className="button is-small is-light mt-2" 
                  onClick={fetchActivities}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Search and Filter Section */}
            <div className="box mb-4">
              <div className="columns">
                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">Category</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          name="category"
                          value={filters.category}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Categories</option>
                          {categoryOptions.map(cat => (
                            <option key={cat} value={cat}>
                              {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">Start Date</label>
                    <div className="control">
                      <input
                        className="input"
                        type="date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">End Date</label>
                    <div className="control">
                      <input
                        className="input"
                        type="date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">&nbsp;</label>
                    <div className="control">
                      <button 
                        className="button is-light is-fullwidth"
                        onClick={clearFilters}
                        disabled={!hasActiveFilters}
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {sortedActivities.length === 0 && !loading && !error ? (
              <div className="notification is-info is-light">
                <p>
                  {hasActiveFilters
                    ? 'No activities match your search criteria.'
                    : 'No activities found.'}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 is-flex is-justify-content-space-between is-align-items-center">
                  <p className="is-size-7">
                    Showing {sortedActivities.length} of {pagination.total} activit{pagination.total !== 1 ? 'ies' : 'y'}
                  </p>
                  {hasActiveFilters && (
                    <div className="tags">
                      {filters.category && (
                        <span className="tag is-info">
                          Category: {filters.category.charAt(0).toUpperCase() + filters.category.slice(1)}
                          <button 
                            className="delete is-small" 
                            onClick={() => setFilters(prev => ({ ...prev, category: '' }))}
                          ></button>
                        </span>
                      )}
                      {filters.startDate && (
                        <span className="tag is-info">
                          From: {filters.startDate}
                          <button 
                            className="delete is-small" 
                            onClick={() => setFilters(prev => ({ ...prev, startDate: '' }))}
                          ></button>
                        </span>
                      )}
                      {filters.endDate && (
                        <span className="tag is-info">
                          To: {filters.endDate}
                          <button 
                            className="delete is-small" 
                            onClick={() => setFilters(prev => ({ ...prev, endDate: '' }))}
                          ></button>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable">
                    <thead>
                      <tr>
                        <th 
                          onClick={() => handleSort('createdAt')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'createdAt' ? 'colorBlack' : 'colorBlue'}
                        >
                          Date/Time {getSortIcon('createdAt')}
                        </th>
                        <th 
                          onClick={() => handleSort('category')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'category' ? 'colorBlack' : 'colorBlue'}
                        >
                          Category {getSortIcon('category')}
                        </th>
                        <th 
                          onClick={() => handleSort('action')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'action' ? 'colorBlack' : 'colorBlue'}
                        >
                          Action {getSortIcon('action')}
                        </th>
                        <th>Description</th>
                        <th 
                          onClick={() => handleSort('performedByName')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'performedByName' ? 'colorBlack' : 'colorBlue'}
                        >
                          Performed By {getSortIcon('performedByName')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedActivities.map((activity) => {
                        const id = activity._id;
                        
                        return (
                          <tr 
                            key={id}
                            style={{ transition: 'background-color 0.1s ease' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#fafafa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <td className="is-size-7">
                              {formatDateTime(activity.createdAt)}
                            </td>
                            <td>
                              <span className={`tag ${getCategoryColor(activity.category)}`}>
                                {activity.category ? activity.category.charAt(0).toUpperCase() + activity.category.slice(1) : 'N/A'}
                              </span>
                            </td>
                            <td className="is-size-7">
                              <code>{activity.action}</code>
                            </td>
                            <td className="is-size-7" style={{ maxWidth: '300px' }}>
                              {activity.description}
                            </td>
                            <td className="is-size-7">
                              <span className="has-text-weight-medium">
                                {activity.performedByName || 'System'}
                              </span>
                              {activity.performedByRole && (
                                <span className="tag is-light is-small ml-1">
                                  {activity.performedByRole.charAt(0).toUpperCase() + activity.performedByRole.slice(1)}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <nav className="pagination is-centered mt-4" role="navigation" aria-label="pagination">
                    <button
                      className="pagination-previous mx-1"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      Previous
                    </button>
                    <button
                      className="pagination-next mx-1"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      Next
                    </button>
                    <ul className="pagination-list">
                      {/* First page */}
                      {pagination.page > 2 && (
                        <>
                          <li>
                            <button
                              className="pagination-link mx-1"
                              onClick={() => handlePageChange(1)}
                            >
                              1
                            </button>
                          </li>
                          {pagination.page > 3 && (
                            <li><span className="pagination-ellipsis">&hellip;</span></li>
                          )}
                        </>
                      )}

                      {/* Previous page */}
                      {pagination.page > 1 && (
                        <li>
                          <button
                            className="pagination-link mx-1"
                            onClick={() => handlePageChange(pagination.page - 1)}
                          >
                            {pagination.page - 1}
                          </button>
                        </li>
                      )}

                      {/* Current page */}
                      <li>
                        <button className="pagination-link mx-1 is-current" aria-current="page">
                          {pagination.page}
                        </button>
                      </li>

                      {/* Next page */}
                      {pagination.page < pagination.totalPages && (
                        <li>
                          <button
                            className="pagination-link mx-1"
                            onClick={() => handlePageChange(pagination.page + 1)}
                          >
                            {pagination.page + 1}
                          </button>
                        </li>
                      )}

                      {/* Last page */}
                      {pagination.page < pagination.totalPages - 1 && (
                        <>
                          {pagination.page < pagination.totalPages - 2 && (
                            <li><span className="pagination-ellipsis">&hellip;</span></li>
                          )}
                          <li>
                            <button
                              className="pagination-link mx-1"
                              onClick={() => handlePageChange(pagination.totalPages)}
                            >
                              {pagination.totalPages}
                            </button>
                          </li>
                        </>
                      )}
                    </ul>
                  </nav>
                )}
              </>
            )}

          </div>

        </div>
      </section>
    </>
  );
};

export default Activity;